import {
  AccessMode,
  OrganizationKind,
  UserRole as Role,
} from '@/types/generated/enums';
import type {
  AccessibleOrganizationRead,
  CurrentUserResponse,
  UserRole,
} from '@/types/generated';

export { Role };

type UserLike =
  | CurrentUserResponse
  | {
      role?: UserRole | null;
      organization?: { access_mode?: AccessMode | null } | null;
    }
  | null;

function roleOf(user?: UserLike): UserRole | undefined {
  return user?.role ?? undefined;
}

export function isAdmin(user?: UserLike): boolean {
  const role = roleOf(user);
  return role === Role.ADMIN || role === Role.SUPER_ADMIN;
}

export function isSuperAdmin(user?: UserLike): boolean {
  return roleOf(user) === Role.SUPER_ADMIN;
}

export function isStandard(user?: UserLike): boolean {
  return roleOf(user) === Role.STANDARD;
}

export function isViewer(user?: UserLike): boolean {
  return roleOf(user) === Role.VIEWER;
}

// Role is the person's rung; access mode is the tenant's write entitlement — kept separate.
export function accessModeOf(user?: UserLike): AccessMode | undefined {
  return user?.organization?.access_mode ?? undefined;
}

export function isReadOnlyOrg(user?: UserLike): boolean {
  return accessModeOf(user) === AccessMode.READ_ONLY;
}

// Tenant-level write gate: a non-Viewer in a FULL organization.
export function canWrite(user?: UserLike): boolean {
  return !isViewer(user) && !isReadOnlyOrg(user);
}

export function canEditOrganization(user?: UserLike): boolean {
  return isAdmin(user) && !isReadOnlyOrg(user);
}

export function canManageMembers(user?: UserLike): boolean {
  return isAdmin(user) && !isReadOnlyOrg(user);
}

export function canManagePlatformConnections(user?: UserLike): boolean {
  return isAdmin(user) && !isReadOnlyOrg(user);
}

// Mirrors the API's AdminRoleDep on POST /campaigns/import/full-config/refresh.
export function canRefreshCampaignConfig(user?: UserLike): boolean {
  return isAdmin(user) && !isReadOnlyOrg(user);
}

// Viewers are read-only everywhere; Standard/Admin can create unless the org is READ_ONLY.
export function canCreateStrategy(user?: UserLike): boolean {
  return canWrite(user);
}

export function canRunManualRefresh(user?: UserLike): boolean {
  return canWrite(user);
}

// Billing is the one write READ_ONLY admins keep: the backend's checkout and portal POSTs
// sit on BillingAdminRoleDep (enforce_access_mode=False), and this mirrors that opt-out.
export function canManageBilling(user?: UserLike): boolean {
  return isAdmin(user);
}

// Release sits on BillingAdminRoleDep too, so a READ_ONLY workspace can still leave its agency.
export function canReleaseClient(user?: UserLike): boolean {
  return canManageBilling(user);
}

// Only a standalone workspace's own admin answers an agency; it skips the read-only gate too.
export function canDecideManagementRequests(
  user?: CurrentUserResponse | null,
): boolean {
  return (
    canManageBilling(user) && !isManagedClient(user) && !isAgencyStaff(user)
  );
}

// Mirrors the API's AdminRoleDep on DELETE /agency/clients/{client_id}.
export function canDeleteClient(user?: UserLike): boolean {
  return isAdmin(user) && !isReadOnlyOrg(user);
}

// Notification read state is user-scoped, ungated on the API — keeps working in READ_ONLY.
export function canManageNotifications(user?: UserLike): boolean {
  return !isViewer(user);
}

// One login reaching several organizations is agency staff; an older API omits the list entirely.
export function canSwitchOrganization(
  user?: CurrentUserResponse | null,
): boolean {
  return (user?.accessible_organizations ?? []).length > 1;
}

// Only a login's own organization can be an agency, so a login reaches at most one.
export function agencyOf(
  user?: CurrentUserResponse | null,
): AccessibleOrganizationRead | undefined {
  return (user?.accessible_organizations ?? []).find(
    (organization) => organization.kind === OrganizationKind.AGENCY,
  );
}

// The API refuses an agency login's requests unless they name an organization.
export function isAgencyStaff(user?: CurrentUserResponse | null): boolean {
  return agencyOf(user) !== undefined;
}

// `organization_id` is the organization this tab acts in, not the login's own.
export function isInAgencyScope(user?: CurrentUserResponse | null): boolean {
  const agency = agencyOf(user);
  return agency !== undefined && agency.id === user?.organization_id;
}

// Agency staff in one of their agency's clients, not in the agency's own tab.
export function actsThroughAssignment(
  user?: CurrentUserResponse | null,
): boolean {
  return isAgencyStaff(user) && !isInAgencyScope(user);
}

// Billed through its agency: a lapse is the agency's to resolve, so no pay path is offered here.
export function isManagedClient(user?: CurrentUserResponse | null): boolean {
  return Boolean(user?.organization?.is_managed);
}
